import type { i18n } from 'i18next';

import { constants } from '@myphx-engineering/phx-design-system/framework';

import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import type {
    AuthorizedMedicationInfoFragment,
    DigitallyReceivedPrescriberOrderBasicInfoFragment,
    MedicationInfoFragment,
    UnclaimedPrescriberOrderInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../graphql/generated/graphql';
import type { MedicationOverride } from '../../../hooks/use-config-api';
import type { BasicPrescriberOrderFragment } from '../../orders';

type OrderType =
    | BasicPrescriberOrderFragment
    | DigitallyReceivedPrescriberOrderBasicInfoFragment
    | UnclaimedPrescriberOrderInfoFragment;

export const getProductDescriptionFromMedication = (
    medication:
        | MedicationInfoFragment
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment
        | undefined,
    i18n: i18n,
    overrides?: MedicationOverride
): string | null | undefined => {
    if (!medication) {
        return overrides?.description ?? null;
    }

    if (medication.__typename === 'UnknownMedication') {
        return (
            overrides?.description ?? medication.unknownMedicationDescription
        );
    }

    const med = medication as
        | MedicationInfoFragment
        | AuthorizedMedicationInfoFragment;

    let medForm;
    switch (med.formCode) {
        case 'SOAJ':
            medForm = i18n.t('formCodes.SOAJ');
            break;
        case 'SOCT':
            medForm = i18n.t('formCodes.SOCT');
            break;
        case 'SOLN':
            medForm = i18n.t('formCodes.SOLN');
            break;
        case 'SOPN':
            medForm = i18n.t('formCodes.SOPN');
            break;
        case 'SOSY':
            medForm = i18n.t('formCodes.SOSY');
            break;
        case 'SUPN':
            medForm = i18n.t('formCodes.SUPN');
            break;
        case 'SUSP':
            medForm = i18n.t('formCodes.SUSP');
            break;
        default:
            medForm = med.formCode;
    }

    const description = `${med.name} ${med.strengthValue}${med.strengthUnit} ${medForm}`;
    const reg = constants.unicode.REGISTERED;

    return description
        .replaceAll('Zepbound', `Zepbound${reg}`)
        .replaceAll('KwikPen', `KwikPen${reg}`)
        .replaceAll(`${reg}${reg}`, reg);
};

// @TODO: this could probably be a type policy on the AuthorizedMedication fragment, the prescriber order, or some similar place
export const getProductDescription = (
    order: OrderType,
    i18n: i18n,
    overrides?: MedicationOverride
): string | null | undefined => {
    if (overrides?.description) {
        return overrides.description;
    }

    if (order.__typename === 'DigitallyReceivedPrescriberOrder') {
        const authorizedMedication = order.authorizedMedication;

        if (authorizedMedication.__typename !== 'AuthorizedMedication') {
            return order.productDescription;
        }

        const med = authorizedMedication as AuthorizedMedicationInfoFragment;

        return getProductDescriptionFromMedication(med, i18n, overrides);
    }

    if (order.__typename === 'UnclaimedPrescriberOrder') {
        if (
            order.prescribedMedicationUnclaimed.__typename ===
            'UnknownMedication'
        ) {
            return order.prescribedMedicationUnclaimed.unknownDescription;
        }

        const med = getFragment(order.prescribedMedicationUnclaimed);

        return getProductDescriptionFromMedication(med, i18n, overrides);
    }

    return null;
};
